import { createContext, useContext, useState, useEffect, ReactNode, useMemo } from 'react';

// Language interface
export interface Language {
  name: string;
  code: string;
  flag: string;
}

// Define translations
export const translations: Record<string, Record<string, string>> = {
  en: {
    // Common/Navbar
    home: "Home",
    destinations: "Destinations",
    services: "Services",
    travelInfo: "Travel Info",
    exploreUganda: "Explore Uganda",
    ecocentric: "Ecocentric",
    blog: "Blog",
    booking: "Booking",
    aboutUs: "About Us",
    wishlist: "Wishlist",
    gallery: "Gallery",
    language: "Language",
    search: "Search tours...",
    
    // Common phrases
    learnMore: "Learn more",
    viewDetails: "View Details",
    bookNow: "Book Now",
    contactUs: "Contact Us",
    readMore: "Read More",
    
    // Footer
    allRightsReserved: "All Rights Reserved",
    privacyPolicy: "Privacy Policy",
    termsOfService: "Terms of Service",
    quickLinks: "Quick Links",
    footerTagline: "Your trusted partner for unforgettable safari adventures in Uganda.",
    
    // Home page
    discoverAfrica: "Discover Africa's Wildlife",
    experienceTravel: "Experience Travel Like Never Before",
    exploreDestinations: "Explore Destinations",
    
    // Hero slider
    murchisonFallsCaption: "Murchison Falls: Nature's Spectacle",
    murchisonFallsDescription: "Witness the Nile River's most breathtaking moment as it thunders through a 7-meter gorge, creating an unforgettable spectacle!",
    maasaiMaraCaption: "Maasai Mara National Reserve: The Great Migration",
    maasaiMaraDescription: "Experience the world's most epic wildlife spectacle as millions of wildebeests, zebras, and gazelles migrate across the savannah.",
    serengetiCaption: "Serengeti National Park: Endless Plains",
    serengetiDescription: "Witness the majestic beauty of the Serengeti, where endless plains stretch as far as the eye can see, teeming with wildlife.",
    volcanoesCaption: "Volcanoes National Park: Gorilla Encounter",
    volcanoesDescription: "Trek through the misty mountains and come face-to-face with the endangered mountain gorillas in their natural habitat.",
    
    // USP Section
    uspTitle: "UNIQUE SELLING PROPOSITION",
    uspAuthenticExperiences: "Authentic Experiences",
    uspAuthenticDescription: "We create authentic, innovative, and high-quality, unforgettable Uganda experiences that leave lasting fond memories.",
    uspTailorMade: "Tailor-made Safaris",
    uspTailorDescription: "Our safari packages are excellently priced and tailored in a way that benefits both the local community and the environment and exceeds our guests' expectations.",
    uspCommunityBased: "Community-Based",
    uspCommunityDescription: "Our tours are strongly connected to the people, we employ Local Guides, book locally owned accommodation, and encourage the local communities to make crafts that they can in turn sell to clients.",
    uspTimelyResponse: "Timely Response",
    uspTimelyDescription: "We are keen on excellent communication, and timely responses.",
    uspFlexibility: "Flexibility",
    uspFlexibilityDescription: "Flexibility, freedom, and fun no matter the travel style, our tours balance well-planned itineraries with the flexibility to do your own thing and make the experience your own.",
    uspEcocentric: "Ecocentric",
    uspEcocentricDescription: "At Ecoplace Tours, we are Eco-centric in all aspects of our doings. We bring all aspects of tourism in one place such as wildlife safaris, accommodation, and many more.",
    
    // Services
    carHire: "Car Hire",
    airTicketing: "Air Ticketing",
    accommodation: "Accommodation",
    wildlifeSafaris: "Wildlife & Birding Safaris",
    gorillaTrekking: "Gorilla & Primate Trekking",
    adventureTours: "Soft & Extreme Adventures",
    culturalTours: "Cultural & Culinary Tours",
    airportTransport: "Airport Transport",
    
    // Tour Showcase
    bestSellingTours: "Best Selling Tours",
    viewTourButton: "View Tour",
    
    // Tour titles
    tourMurchisonTitle: "3-Day Murchison Falls Adventure",
    tourMurchisonDesc: "Experience the magnificent Murchison Falls National Park, with game drives and a Nile boat cruise",
    tourBwindiTitle: "5-Day Bwindi Gorilla Trekking",
    tourBwindiDesc: "Meet the endangered mountain gorillas in their natural habitat in Bwindi Impenetrable Forest",
    tourMasaiTitle: "4-Day Masai Mara Safari",
    tourMasaiDesc: "Witness the incredible wildlife of Kenya's most famous reserve, home to the Great Migration",
    tourSerengetiTitle: "5-Day Serengeti & Ngorongoro",
    tourSerengetiDesc: "Explore the endless plains of Serengeti and the marvel of Ngorongoro Crater",
    tourQueenElizabethTitle: "3-Day Queen Elizabeth National Park",
    tourQueenElizabethDesc: "Encounter diverse wildlife, including the famous tree-climbing lions of Ishasha",
    tourRwandaTitle: "4-Day Rwanda Gorilla & Golden Monkey",
    tourRwandaDesc: "Track endangered gorillas and playful golden monkeys in the misty forests of Volcanoes National Park",
    
    // Tour Page
    tourOverview: "Tour Overview",
    dailyItinerary: "Daily Itinerary",
    bookThisTour: "Book This Tour",
    yourName: "Your Name",
    emailAddress: "Email Address",
    phoneNumber: "Phone Number",
    pickDate: "Pick a date",
    numberOfTravelers: "Number of Travelers",
    specialRequirements: "Special Requirements",
    sending: "Sending...",
    requestBooking: "Request Booking",
    inclusions: "Inclusions",
    exclusions: "Exclusions",
    similarTours: "Similar Tours You Might Like",
    discoverMoreTours: "Discover more amazing safari experiences across {country} with our curated selection of tours",
    viewTour: "View Tour",
    
    // Component translations
    ourPartners: "Our Partners",
    ourPaymentOptions: "Our Payment Options",
    whatOurClientsSay: "What Our Clients Say",
    clientReviewsSubtitle: "Read about the amazing experiences our clients have had on their African adventures",
    tour: "Tour",
    
    // Home page sections
    bestSellingItineraries: "Best-Selling Itineraries",
    bestSellingItinerariesSubtitle: "Experience Uganda's most popular and carefully crafted safari adventures",
    servicesSubtitle: "We provide comprehensive safari services to ensure your journey is comfortable and memorable",
  },
  fr: {
    // Common/Navbar
    home: "Accueil",
    destinations: "Destinations",
    services: "Services",
    travelInfo: "Infos Voyage",
    exploreUganda: "Explorer l'Ouganda",
    ecocentric: "Écocentrique",
    blog: "Blog",
    booking: "Réservation",
    aboutUs: "À Propos",
    wishlist: "Favoris",
    gallery: "Galerie",
    language: "Langue",
    search: "Rechercher des circuits...",
    
    // Common phrases
    learnMore: "En savoir plus",
    viewDetails: "Voir les détails",
    bookNow: "Réserver",
    contactUs: "Contactez-nous",
    readMore: "Lire la suite",
    
    // Footer
    allRightsReserved: "Tous droits réservés",
    privacyPolicy: "Politique de confidentialité",
    termsOfService: "Conditions d'utilisation",
    quickLinks: "Liens Rapides",
    footerTagline: "Votre partenaire de confiance pour des aventures safari inoubliables en Ouganda.",
    
    // Home page
    discoverAfrica: "Découvrez la Faune Africaine",
    experienceTravel: "Voyagez Comme Jamais Auparavant",
    exploreDestinations: "Explorer les Destinations",
    
    // Hero slider
    murchisonFallsCaption: "Chutes Murchison: Spectacle de la Nature",
    murchisonFallsDescription: "Observez le moment le plus époustouflant du Nil lorsqu'il traverse un défilé de 7 mètres, créant un spectacle inoubliable!",
    maasaiMaraCaption: "Réserve Nationale de Maasai Mara: La Grande Migration",
    maasaiMaraDescription: "Vivez le spectacle animalier le plus épique au monde où des millions de gnous, zèbres et gazelles migrent à travers la savane.",
    serengetiCaption: "Parc National du Serengeti: Plaines Sans Fin",
    serengetiDescription: "Admirez la beauté majestueuse du Serengeti, où des plaines infinies s'étendent à perte de vue, fourmillant de vie sauvage.",
    volcanoesCaption: "Parc National des Volcans: Rencontre avec les Gorilles",
    volcanoesDescription: "Traversez les montagnes brumeuses et venez face à face avec les gorilles de montagne en voie de disparition dans leur habitat naturel.",
    
    // USP Section
    uspTitle: "PROPOSITION DE VENTE UNIQUE",
    uspAuthenticExperiences: "Expériences Authentiques",
    uspAuthenticDescription: "Nous créons des expériences ougandaises authentiques, innovantes et de haute qualité, inoubliables qui laissent des souvenirs durables.",
    uspTailorMade: "Safaris Sur Mesure",
    uspTailorDescription: "Nos forfaits safari sont excellemment tarifés et adaptés de manière à bénéficier à la fois à la communauté locale et à l'environnement et à dépasser les attentes de nos clients.",
    uspCommunityBased: "Basé sur la Communauté",
    uspCommunityDescription: "Nos circuits sont fortement liés aux populations locales, nous employons des guides locaux, réservons des hébergements appartenant à des locaux et encourageons les communautés à fabriquer des objets artisanaux qu'elles peuvent vendre aux clients.",
    uspTimelyResponse: "Réponse Rapide",
    uspTimelyDescription: "Nous sommes attentifs à une excellente communication et à des réponses rapides.",
    uspFlexibility: "Flexibilité",
    uspFlexibilityDescription: "Flexibilité, liberté et plaisir, quel que soit le style de voyage, nos circuits équilibrent des itinéraires bien planifiés avec la flexibilité de faire votre propre chose et de rendre l'expérience unique.",
    uspEcocentric: "Écocentrique",
    uspEcocentricDescription: "Chez Ecoplace Tours, nous sommes écocentriques dans tous les aspects de nos activités. Nous réunissons tous les aspects du tourisme en un seul endroit comme les safaris animaliers, l'hébergement, et bien plus encore.",
    
    // Services
    carHire: "Location de Voiture",
    airTicketing: "Billetterie Aérienne",
    accommodation: "Hébergement",
    wildlifeSafaris: "Safaris Animaliers & Ornithologiques",
    gorillaTrekking: "Trekking de Gorilles & Primates",
    adventureTours: "Aventures Douces & Extrêmes",
    culturalTours: "Circuits Culturels & Culinaires",
    airportTransport: "Transport Aéroport",
    
    // Tour Showcase
    bestSellingTours: "Circuits les Plus Populaires",
    viewTourButton: "Voir le Circuit",
    
    // Tour titles
    tourMurchisonTitle: "Aventure aux Chutes Murchison - 3 Jours",
    tourMurchisonDesc: "Découvrez le magnifique Parc National des Chutes Murchison, avec safaris et croisière sur le Nil",
    tourBwindiTitle: "Trekking Gorilles à Bwindi - 5 Jours",
    tourBwindiDesc: "Rencontrez les gorilles de montagne en voie de disparition dans leur habitat naturel dans la Forêt Impénétrable de Bwindi",
    tourMasaiTitle: "Safari au Masai Mara - 4 Jours",
    tourMasaiDesc: "Observez l'incroyable faune de la réserve la plus célèbre du Kenya, terre de la Grande Migration",
    tourSerengetiTitle: "Serengeti & Ngorongoro - 5 Jours",
    tourSerengetiDesc: "Explorez les plaines infinies du Serengeti et la merveille du Cratère du Ngorongoro",
    tourQueenElizabethTitle: "Parc National Queen Elizabeth - 3 Jours",
    tourQueenElizabethDesc: "Rencontrez une faune diverse, dont les célèbres lions grimpeurs d'Ishasha",
    tourRwandaTitle: "Gorilles et Singes Dorés du Rwanda - 4 Jours",
    tourRwandaDesc: "Suivez les gorilles en voie de disparition et les singes dorés espiègles dans les forêts brumeuses du Parc National des Volcans",
    
    // Tour Page
    tourOverview: "Aperçu du Circuit",
    dailyItinerary: "Itinéraire Quotidien",
    bookThisTour: "Réserver Ce Circuit",
    yourName: "Votre Nom",
    emailAddress: "Adresse Email",
    phoneNumber: "Numéro de Téléphone",
    pickDate: "Choisir une date",
    numberOfTravelers: "Nombre de Voyageurs",
    specialRequirements: "Besoins Spécifiques",
    sending: "Envoi en cours...",
    requestBooking: "Demander une Réservation",
    inclusions: "Inclusions",
    exclusions: "Exclusions",
    similarTours: "Circuits Similaires Qui Pourraient Vous Plaire",
    discoverMoreTours: "Découvrez plus d'expériences safari incroyables à travers {country} avec notre sélection de circuits organisés",
    viewTour: "Voir le Circuit",
    
    // Component translations
    ourPartners: "Nos Partenaires",
    ourPaymentOptions: "Nos Options de Paiement",
    whatOurClientsSay: "Ce Que Disent Nos Clients",
    clientReviewsSubtitle: "Lisez les expériences incroyables que nos clients ont vécues lors de leurs aventures africaines",
    tour: "Circuit",
    
    // Home page sections
    bestSellingItineraries: "Itinéraires les Plus Populaires",
    bestSellingItinerariesSubtitle: "Découvrez les safaris les plus populaires et soigneusement élaborés d'Ouganda",
    servicesSubtitle: "Nous fournissons des services safari complets pour garantir que votre voyage soit confortable et mémorable",
  },
  es: {
    // Common/Navbar
    home: "Inicio",
    destinations: "Destinos",
    services: "Servicios",
    travelInfo: "Info de Viaje",
    exploreUganda: "Explorar Uganda",
    ecocentric: "Ecocéntrico",
    blog: "Blog",
    booking: "Reservas",
    aboutUs: "Nosotros",
    wishlist: "Favoritos",
    gallery: "Galería",
    language: "Idioma",
    search: "Buscar tours...",
    
    // Common phrases
    learnMore: "Más información",
    viewDetails: "Ver detalles",
    bookNow: "Reservar Ahora",
    contactUs: "Contáctenos",
    readMore: "Leer más",
    
    // Footer
    allRightsReserved: "Todos los derechos reservados",
    privacyPolicy: "Política de privacidad",
    termsOfService: "Términos de servicio",
    
    // Home page
    discoverAfrica: "Descubre la Fauna Africana",
    experienceTravel: "Experimenta Viajes Como Nunca Antes",
    exploreDestinations: "Explorar Destinos",
    
    // Services
    carHire: "Alquiler de Coches",
    airTicketing: "Boletos Aéreos",
    accommodation: "Alojamiento",
    wildlifeSafaris: "Safaris de Vida Silvestre y Aves",
    gorillaTrekking: "Trekking de Gorilas y Primates",
    adventureTours: "Aventuras Suaves y Extremas",
    culturalTours: "Tours Culturales y Culinarios",
    airportTransport: "Transporte al Aeropuerto",
  },
  de: {
    // Common/Navbar
    home: "Startseite",
    destinations: "Reiseziele",
    services: "Dienstleistungen",
    travelInfo: "Reiseinfo",
    exploreUganda: "Uganda entdecken",
    ecocentric: "Ökozentrisch",
    blog: "Blog",
    booking: "Buchung",
    aboutUs: "Über uns",
    wishlist: "Merkliste",
    gallery: "Galerie",
    language: "Sprache",
    search: "Touren suchen...",
    
    // Common phrases
    learnMore: "Mehr erfahren",
    viewDetails: "Details anzeigen",
    bookNow: "Jetzt buchen",
    contactUs: "Kontaktieren Sie uns",
    readMore: "Weiterlesen",
    
    // Footer
    allRightsReserved: "Alle Rechte vorbehalten",
    privacyPolicy: "Datenschutzrichtlinie",
    termsOfService: "Nutzungsbedingungen",
    
    // Home page
    discoverAfrica: "Entdecken Sie Afrikas Tierwelt",
    experienceTravel: "Erleben Sie Reisen wie nie zuvor",
    exploreDestinations: "Reiseziele erkunden",
    
    // Services
    carHire: "Autovermietung",
    airTicketing: "Flugtickets",
    accommodation: "Unterkunft",
    wildlifeSafaris: "Wildlife- & Vogelbeobachtungssafaris",
    gorillaTrekking: "Gorilla- & Primaten-Trekking",
    adventureTours: "Sanfte & Extreme Abenteuer",
    culturalTours: "Kultur- & Kulinarische Touren",
    airportTransport: "Flughafentransfer",
  },
  it: {
    // Common/Navbar
    home: "Home",
    destinations: "Destinazioni",
    services: "Servizi",
    travelInfo: "Info Viaggio",
    exploreUganda: "Esplora Uganda",
    ecocentric: "Ecocentrico",
    blog: "Blog",
    booking: "Prenotazioni",
    aboutUs: "Chi Siamo",
    wishlist: "Preferiti",
    gallery: "Galleria",
    language: "Lingua",
    search: "Cerca tour...",
    
    // Common phrases
    learnMore: "Scopri di più",
    viewDetails: "Vedi dettagli",
    bookNow: "Prenota ora",
    contactUs: "Contattaci",
    readMore: "Leggi di più",
    
    // Footer
    allRightsReserved: "Tutti i diritti riservati",
    privacyPolicy: "Informativa sulla privacy",
    termsOfService: "Termini di servizio",
    
    // Home page
    discoverAfrica: "Scopri la fauna africana",
    experienceTravel: "Vivi il viaggio come mai prima",
    exploreDestinations: "Esplora le destinazioni",
    
    // Services
    carHire: "Noleggio Auto",
    airTicketing: "Biglietteria Aerea",
    accommodation: "Alloggio",
    wildlifeSafaris: "Safari Fauna Selvatica & Birdwatching",
    gorillaTrekking: "Trekking Gorilla & Primati",
    adventureTours: "Avventure Soft & Estreme",
    culturalTours: "Tour Culturali & Culinari",
    airportTransport: "Trasporto Aeroportuale",
  },
  nl: {
    // Common/Navbar
    home: "Home",
    destinations: "Bestemmingen",
    services: "Diensten",
    travelInfo: "Reisinformatie",
    exploreUganda: "Ontdek Oeganda",
    ecocentric: "Ecocentrisch",
    blog: "Blog",
    booking: "Boekingen",
    aboutUs: "Over Ons",
    wishlist: "Verlanglijst",
    gallery: "Galerij",
    language: "Taal",
    search: "Zoek tours...",
    
    // Common phrases
    learnMore: "Meer informatie",
    viewDetails: "Details bekijken",
    bookNow: "Nu boeken",
    contactUs: "Contact ons",
    readMore: "Lees meer",
    
    // Footer
    allRightsReserved: "Alle rechten voorbehouden",
    privacyPolicy: "Privacybeleid",
    termsOfService: "Servicevoorwaarden",
    
    // Home page
    discoverAfrica: "Ontdek de Afrikaanse wildernis",
    experienceTravel: "Ervaar reizen als nooit tevoren",
    exploreDestinations: "Verken bestemmingen",
    
    // Services
    carHire: "Auto verhuur",
    airTicketing: "Vliegtickets",
    accommodation: "Accommodatie",
    wildlifeSafaris: "Wildlife & Vogelspotten Safaris",
    gorillaTrekking: "Gorilla & Primaten Trekking",
    adventureTours: "Zachte & Extreme Avonturen",
    culturalTours: "Culturele & Culinaire Tours",
    airportTransport: "Luchthaventransport",
  },
  pt: {
    // Common/Navbar
    home: "Início",
    destinations: "Destinos",
    services: "Serviços",
    travelInfo: "Info de Viagem",
    exploreUganda: "Explorar Uganda",
    ecocentric: "Ecocêntrico",
    blog: "Blog",
    booking: "Reservas",
    aboutUs: "Sobre Nós",
    wishlist: "Favoritos",
    gallery: "Galeria",
    language: "Idioma",
    search: "Buscar tours...",
    
    // Common phrases
    learnMore: "Saiba mais",
    viewDetails: "Ver detalhes",
    bookNow: "Reservar Agora",
    contactUs: "Entre em contato",
    readMore: "Leia mais",
    
    // Footer
    allRightsReserved: "Todos os direitos reservados",
    privacyPolicy: "Política de privacidade",
    termsOfService: "Termos de serviço",
    
    // Home page
    discoverAfrica: "Descubra a Vida Selvagem Africana",
    experienceTravel: "Experimente Viagens Como Nunca Antes",
    exploreDestinations: "Explorar Destinos",
    
    // Services
    carHire: "Aluguel de Carros",
    airTicketing: "Bilhetes Aéreos",
    accommodation: "Hospedagem",
    wildlifeSafaris: "Safaris de Vida Selvagem & Observação de Aves",
    gorillaTrekking: "Trekking de Gorilas & Primatas",
    adventureTours: "Aventuras Suaves & Extremas",
    culturalTours: "Tours Culturais & Culinários",
    airportTransport: "Transporte para o Aeroporto",
  },
  zh: {
    // Common/Navbar
    home: "首页",
    destinations: "目的地",
    services: "服务",
    travelInfo: "旅行信息",
    exploreUganda: "探索乌干达",
    ecocentric: "生态中心",
    blog: "博客",
    booking: "预订",
    aboutUs: "关于我们",
    wishlist: "收藏夹",
    gallery: "画廊",
    language: "语言",
    search: "搜索旅游...",
    
    // Common phrases
    learnMore: "了解更多",
    viewDetails: "查看详情",
    bookNow: "立即预订",
    contactUs: "联系我们",
    readMore: "阅读更多",
    
    // Footer
    allRightsReserved: "版权所有",
    privacyPolicy: "隐私政策",
    termsOfService: "服务条款",
    
    // Home page
    discoverAfrica: "探索非洲野生动物",
    experienceTravel: "前所未有的旅行体验",
    exploreDestinations: "探索目的地",
    
    // Services
    carHire: "汽车租赁",
    airTicketing: "机票预订",
    accommodation: "住宿",
    wildlifeSafaris: "野生动物与观鸟旅行",
    gorillaTrekking: "大猩猩与灵长类动物追踪",
    adventureTours: "温和与极限冒险",
    culturalTours: "文化与美食之旅",
    airportTransport: "机场接送",
  },
  ja: {
    // Common/Navbar
    home: "ホーム",
    destinations: "目的地",
    services: "サービス",
    travelInfo: "旅行情報",
    exploreUganda: "ウガンダを探る",
    ecocentric: "エコセントリック",
    blog: "ブログ",
    booking: "予約",
    aboutUs: "会社概要",
    wishlist: "お気に入り",
    gallery: "ギャラリー",
    language: "言語",
    search: "ツアーを検索...",
    
    // Common phrases
    learnMore: "詳細を見る",
    viewDetails: "詳細を表示",
    bookNow: "今すぐ予約",
    contactUs: "お問い合わせ",
    readMore: "続きを読む",
    
    // Footer
    allRightsReserved: "全著作権所有",
    privacyPolicy: "プライバシーポリシー",
    termsOfService: "利用規約",
    
    // Home page
    discoverAfrica: "アフリカの野生動物を発見",
    experienceTravel: "かつてない旅行体験",
    exploreDestinations: "目的地を探る",
    
    // Services
    carHire: "レンタカー",
    airTicketing: "航空券",
    accommodation: "宿泊施設",
    wildlifeSafaris: "野生動物＆バードウォッチングサファリ",
    gorillaTrekking: "ゴリラ＆霊長類トレッキング",
    adventureTours: "ソフト＆エクストリームアドベンチャー",
    culturalTours: "文化＆料理ツアー",
    airportTransport: "空港送迎",
  },
  ar: {
    // Common/Navbar
    home: "الرئيسية",
    destinations: "الوجهات",
    services: "الخدمات",
    travelInfo: "معلومات السفر",
    exploreUganda: "استكشف أوغندا",
    ecocentric: "مركز بيئي",
    blog: "المدونة",
    booking: "الحجز",
    aboutUs: "من نحن",
    wishlist: "المفضلة",
    gallery: "معرض الصور",
    language: "اللغة",
    search: "بحث عن جولات...",
    
    // Common phrases
    learnMore: "اعرف المزيد",
    viewDetails: "عرض التفاصيل",
    bookNow: "احجز الآن",
    contactUs: "اتصل بنا",
    readMore: "قراءة المزيد",
    
    // Footer
    allRightsReserved: "جميع الحقوق محفوظة",
    privacyPolicy: "سياسة الخصوصية",
    termsOfService: "شروط الخدمة",
    
    // Home page
    discoverAfrica: "اكتشف الحياة البرية في أفريقيا",
    experienceTravel: "تجربة السفر كما لم تعرفها من قبل",
    exploreDestinations: "استكشف الوجهات",
    
    // Services
    carHire: "تأجير السيارات",
    airTicketing: "تذاكر الطيران",
    accommodation: "الإقامة",
    wildlifeSafaris: "رحلات سفاري للحياة البرية ومراقبة الطيور",
    gorillaTrekking: "تتبع الغوريلا والرئيسيات",
    adventureTours: "مغامرات خفيفة ومتطرفة",
    culturalTours: "جولات ثقافية وطهي",
    airportTransport: "نقل المطار",
  },
  ru: {
    // Common/Navbar
    home: "Главная",
    destinations: "Направления",
    services: "Услуги",
    travelInfo: "Информация о поездке",
    exploreUganda: "Исследуйте Уганду",
    ecocentric: "Экоцентрический",
    blog: "Блог",
    booking: "Бронирование",
    aboutUs: "О нас",
    wishlist: "Избранное",
    gallery: "Галерея",
    language: "Язык",
    search: "Поиск туров...",
    
    // Common phrases
    learnMore: "Узнать больше",
    viewDetails: "Посмотреть детали",
    bookNow: "Забронировать сейчас",
    contactUs: "Связаться с нами",
    readMore: "Читать далее",
    
    // Footer
    allRightsReserved: "Все права защищены",
    privacyPolicy: "Политика конфиденциальности",
    termsOfService: "Условия использования",
    
    // Home page
    discoverAfrica: "Откройте для себя дикую природу Африки",
    experienceTravel: "Испытайте путешествие как никогда раньше",
    exploreDestinations: "Исследуйте направления",
    
    // Services
    carHire: "Аренда автомобилей",
    airTicketing: "Авиабилеты",
    accommodation: "Проживание",
    wildlifeSafaris: "Сафари с дикой природой и наблюдением за птицами",
    gorillaTrekking: "Трекинг горилл и приматов",
    adventureTours: "Мягкие и экстремальные приключения",
    culturalTours: "Культурные и кулинарные туры",
    airportTransport: "Транспорт в аэропорт",
  },
  sw: {
    // Common/Navbar
    home: "Nyumbani",
    destinations: "Maeneo ya Ziara",
    services: "Huduma",
    travelInfo: "Maelezo ya Usafiri",
    exploreUganda: "Gundua Uganda",
    ecocentric: "Ekosentriki",
    blog: "Blogu",
    booking: "Uhifadhi",
    aboutUs: "Kuhusu Sisi",
    wishlist: "Vipendwa",
    gallery: "Matunzio",
    language: "Lugha",
    search: "Tafuta ziara...",
    
    // Common phrases
    learnMore: "Jifunze zaidi",
    viewDetails: "Ona maelezo",
    bookNow: "Hifadhi Sasa",
    contactUs: "Wasiliana nasi",
    readMore: "Soma zaidi",
    
    // Footer
    allRightsReserved: "Haki zote zimehifadhiwa",
    privacyPolicy: "Sera ya Faragha",
    termsOfService: "Masharti ya Huduma",
    
    // Home page
    discoverAfrica: "Gundua Wanyama wa Afrika",
    experienceTravel: "Pata Uzoefu wa Usafiri Usio wa Kawaida",
    exploreDestinations: "Gundua Maeneo ya Ziara",
    
    // Services
    carHire: "Kukodi Gari",
    airTicketing: "Tiketi za Ndege",
    accommodation: "Malazi",
    wildlifeSafaris: "Safari za Wanyamapori na Kutazama Ndege",
    gorillaTrekking: "Kutembea kwa Gorila na Primati",
    adventureTours: "Ziara za Hatari Ndogo na Kubwa",
    culturalTours: "Ziara za Kitamaduni na Upishi",
    airportTransport: "Usafiri wa Uwanja wa Ndege",
  }
};

// Define the language context type
interface LanguageContextType {
  currentLanguage: string; 
  setLanguage: (lang: string) => void;
  t: (key: string, vars?: Record<string, string>) => string;
  languages: Language[];
}

// Create a default context value
const defaultContext: LanguageContextType = {
  currentLanguage: 'en',
  setLanguage: () => {},
  t: (key) => key,
  languages: []
};

// Create context with default value
export const LanguageContext = createContext<LanguageContextType>(defaultContext);

// Custom hook for accessing the language context
export const useLanguage = () => {
  const context = useContext(LanguageContext);
  if (context === defaultContext) {
    console.warn('useLanguage: Using default context. Make sure LanguageProvider is in your component tree.');
  }
  return context;
};

// Provider component
interface LanguageProviderProps {
  children: ReactNode;
  languages: Language[];
}

export const LanguageProvider: React.FC<LanguageProviderProps> = ({ children, languages }) => {
  // Get stored language from localStorage or default to 'en'
  const [currentLanguage, setCurrentLanguage] = useState<string>(() => {
    try {
      const storedLanguage = localStorage.getItem('ecoplace-language');
      return storedLanguage || 'en';
    } catch (error) {
      console.error('Failed to get language from localStorage:', error);
      return 'en';
    }
  });

  // Update localStorage whenever language changes
  useEffect(() => {
    try {
      localStorage.setItem('ecoplace-language', currentLanguage);
    } catch (error) {
      console.error('Failed to save language to localStorage:', error);
    }
  }, [currentLanguage]);

  // Translate function
  const translate = (key: string, vars: Record<string, string> = {}): string => {
    try {
      // Get translations for current language or fallback to English
      const langTranslations = translations[currentLanguage] || translations.en;
      
      // Get translation or fallback to English or key itself
      let text = langTranslations[key] || translations.en[key] || key;
      
      // Replace variables in text
      if (vars && Object.keys(vars).length > 0) {
        Object.entries(vars).forEach(([varKey, value]) => {
          text = text.replace(new RegExp(`{${varKey}}`, 'g'), value);
        });
      }
      
      return text;
    } catch (error) {
      console.error(`Translation error for key: ${key}`, error);
      return key; // Fallback to key if error
    }
  };

  // Memoize context value to prevent unnecessary re-renders
  const contextValue = useMemo(() => ({
    currentLanguage,
    setLanguage: setCurrentLanguage,
    t: translate,
    languages
  }), [currentLanguage, languages]);

  return (
    <LanguageContext.Provider value={contextValue}>
      {children}
    </LanguageContext.Provider>
  );
};